import {Calendar} from "lucide-react";
import {Badge} from "../../../basic/badge";
import {Input} from "../../../basic/input";
import {Label} from "../../../basic/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../../../basic/select";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {VwpLicenseRead} from "../../../../store/generated/api";
import {formatDate} from "../../../../utils/dateUtils";
import {CollapsibleSection} from "../../../shared/CollapsibleSection";

interface LicenseFormProps {
    selectedLicense: VwpLicenseRead;
    editedLicenseData: Record<string, any>;
    setEditedLicenseData: (data: Record<string, any>) => void;
}

export function LicenseForm({
                                selectedLicense,
                                editedLicenseData,
                                setEditedLicenseData
                            }: LicenseFormProps) {
    const {t} = useTranslationWithKeys();

    return (
        <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label>{t('admin.licenses.editLicense.product')}</Label>
                    <div className="p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center gap-2">
                            <span>{selectedLicense.product || 'N/A'}</span>
                            {selectedLicense.supportLevel === 'SMA' && (
                                <Badge variant="secondary" className="text-xs">
                                    {t('licenses.sma')}
                                </Badge>
                            )}
                            {selectedLicense.supportLevel === 'SMA_PLUS' && (
                                <Badge variant="secondary" className="text-xs">
                                    {t('licenses.smaPlus')}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="quantity">{t('admin.licenses.editLicense.numberOfSeats')}</Label>
                    <Input
                        id="quantity"
                        type="number"
                        min="1"
                        value={editedLicenseData.quantity || ""}
                        onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (!isNaN(value) && value >= 1) {
                                setEditedLicenseData({...editedLicenseData, quantity: value});
                            }
                        }}
                        placeholder={t('admin.licenses.editLicense.enterNumberOfSeats')}
                    />
                    <p className="text-muted-foreground">
                        {t('admin.licenses.editLicense.seatsDescription')}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label
                        htmlFor="license-status">{t('admin.licenses.editLicense.licenseStatus')}</Label>
                    <Select
                        value={editedLicenseData.status || "ACTIVE"}
                        onValueChange={(value) => setEditedLicenseData({
                            ...editedLicenseData,
                            status: value
                        })}
                    >
                        <SelectTrigger id="license-status">
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ACTIVE">{t('admin.licenses.status.active')}</SelectItem>
                            <SelectItem value="DISABLED">{t('admin.licenses.status.disabled')}</SelectItem>
                        </SelectContent>
                    </Select>
                    <p className="text-muted-foreground">
                        {t('admin.licenses.editLicense.statusDescription')}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="trial-status">{t('admin.licenses.editLicense.trialStatus')}</Label>
                    <Select
                        value={editedLicenseData.trialStatus || "NO_TRIAL"}
                        onValueChange={(value) => setEditedLicenseData({
                            ...editedLicenseData,
                            trialStatus: value
                        })}
                    >
                        <SelectTrigger id="trial-status">
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="NO_TRIAL">{t('licenses.trialStatus.noTrial')}</SelectItem>
                            <SelectItem value="IN_TRIAL">{t('licenses.trialStatus.inTrial')}</SelectItem>
                            <SelectItem value="TRIAL_COMPLETED">{t('licenses.trialStatus.trialCompleted')}</SelectItem>
                        </SelectContent>
                    </Select>
                    <p className="text-muted-foreground">
                        {t('admin.licenses.editLicense.trialStatusDescription')}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label>{t('admin.licenses.editLicense.currentUsage')}</Label>
                    <div className="p-4 rounded-lg border bg-muted/30 space-y-2">
                        <div className="flex justify-between">
                            <span>{t('admin.licenses.editLicense.assignedSeats')}:</span>
                            <span
                                className={selectedLicense.assigned === selectedLicense.quantity ? "text-orange-600" : ""}>
                {selectedLicense.assigned || 0} / {selectedLicense.quantity || 0}
              </span>
                        </div>
                        <div className="flex justify-between">
                            <span>{t('admin.licenses.editLicense.availableSeats')}:</span>
                            <span>
                {(selectedLicense.quantity || 0) - (selectedLicense.assigned || 0)}
              </span>
                        </div>
                        {(selectedLicense.assigned || 0) === (selectedLicense.quantity || 0) && (
                            <p className="text-orange-600 pt-2 border-t">
                                ⚠️ {t('admin.licenses.editLicense.allSeatsAssigned')}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                <div className="space-y-2">
                    <Label>{t('admin.licenses.editLicense.expirationDate')}</Label>
                    <div className="p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground"/>
                            <span>{formatDate(selectedLicense.effectiveExpiryDate)}</span>
                        </div>
                    </div>
                    <p className="text-muted-foreground">
                        {t('admin.licenses.editLicense.expirationDescription')}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label>{t('admin.licenses.editLicense.supportExpiration')}</Label>
                    <div className="p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground"/>
                            <span>{formatDate(selectedLicense.supportExpiration)}</span>
                        </div>
                    </div>
                    <p className="text-muted-foreground">
                        {t('admin.licenses.editLicense.supportExpirationDescription')}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label>{t('admin.licenses.editLicense.softwareExpiration')}</Label>
                    <div className="p-3 rounded-lg border bg-muted/30">
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground"/>
                            <span>{formatDate(selectedLicense.softwareExpiration)}</span>
                        </div>
                    </div>
                    <p className="text-muted-foreground">
                        {t('admin.licenses.editLicense.softwareExpirationDescription')}
                    </p>
                </div>

                <CollapsibleSection
                    title={t('admin.licenses.editLicense.migrationData')}
                    defaultOpen={false}
                >
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="old-pkid">{t('admin.licenses.editLicense.oldPkid')}</Label>
                            <Input
                                id="old-pkid"
                                type="text"
                                value={selectedLicense.oldPkid || ""}
                                readOnly
                                className="bg-muted/30"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="old-eid">{t('admin.licenses.editLicense.oldEid')}</Label>
                            <Input
                                id="old-eid"
                                type="text"
                                value={selectedLicense.oldEid || ""}
                                readOnly
                                className="bg-muted/30"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="migration-crm-id">{t('admin.licenses.editLicense.migrationCrmId')}</Label>
                            <Input
                                id="migration-crm-id"
                                type="text"
                                value={selectedLicense.migrationCrmId || ""}
                                readOnly
                                className="bg-muted/30"
                            />
                        </div>
                    </div>
                </CollapsibleSection>
            </div>
        </div>
    );
}